import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

const DEFAULT_TIME_DURATION: number = 60; // 60 minutes

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private cache: Map<string, unknown> = new Map();
  private cacheDuration: Map<string, number> = new Map();

  has(key: string): boolean {
    this.cacheCleanUp();
    return this.cache.get(key) !== undefined;
  }

  get<T>(key: string): T | null {
    this.cacheCleanUp();
    return this.cache.get(key) as T || null;
  }

  set<T>(key: string, value: T, minutes: number = DEFAULT_TIME_DURATION): void {
    this.cacheCleanUp();
    this.cache.set(key, value);
    this.setCacheDuration(key, minutes);
  }

  delete(key: string): void {
    this.cacheDuration.delete(key);
    this.cache.delete(key);
  }

  deleteAll(): void {
    this.cache.clear();
    this.cacheDuration.clear();
  }

  getCachedObservable<T>(key: string, observable$: Observable<T>, minutes?: number): Observable<T> {
    if (!this.has(key)) {
      const cache$: Observable<T> = observable$
        .pipe(
          shareReplay({ bufferSize: 1, refCount: false })
        );

      this.set<Observable<T>>(key, cache$, minutes);
    }

    return this.get<Observable<T>>(key);
  }

  private setCacheDuration(key: string, minutes: number = DEFAULT_TIME_DURATION): void {
    if (minutes !== 0) {
      this.cacheDuration.set(key, Date.now() + minutes * 60000);
    }
  }

  private cacheCleanUp(): void {
    const now: number = Date.now();
    Array.from(this.cacheDuration.keys()).forEach((key: string) => {
      if (this.cacheDuration.get(key) < now) {
        this.delete(key);
      }
    });
  }
}
